import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { createSharedLink, deleteSharedLink, listSharedLinks, revokeSharedLink, sharedLinkOptions, sharedLinkPreview } from 'api/features';
import { GREEN, MONO, SEV_COLOR, ago } from './scanUi';
import { EXPIRY, STATE, TYPES, expiryAt, expiryText, stateOf, summary, viewsText } from './sharedLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const fail = (e, fb) => e?.response?.data?.detail || Object.values(e?.response?.data || {}).flat()[0] || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;
const Choice = ({ on, onClick, children }) => <Button onClick={onClick} sx={{ borderRadius: 99, px: 2, textTransform: 'none', fontWeight: 600, border: 1, borderColor: on ? GREEN : 'divider', color: on ? GREEN : 'text.secondary' }}>{children}</Button>;
const KIND_COLOR = { scan: '#60a5fa', finding: '#f97316', case: '#a78bfa' };

// What the other person will see, in the same shape as the public page.
function Preview({ type, data, label }) {
  const r = data.resource;
  return (
    <Stack spacing={1.2}>
      <Typography variant="overline" color="text.secondary">What the reader sees</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{label || r.title || r.target}</Typography>
      {type === 'scan' && r.findings && (
        <>
          <Typography variant="body2" color="text.secondary">{r.scan_type?.replace(/_/g, ' ')} on {r.target} · {r.status}</Typography>
          <Stack direction="row" gap={0.8} flexWrap="wrap">
            {Object.entries(r.findings.counts).filter(([, n]) => n).map(([sev, n]) => <Pill key={sev} color={SEV_COLOR[sev]}>{n} {sev.toUpperCase()}</Pill>)}
            {!r.findings.total && <Typography variant="body2" color="text.secondary">No findings.</Typography>}
          </Stack>
          {r.findings.worst.slice(0, 5).map((f, i) => (
            <Stack key={i} direction="row" justifyContent="space-between" gap={1} sx={{ py: 0.8, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2">{f.title}</Typography><Pill color={SEV_COLOR[f.severity]}>{f.severity.toUpperCase()}</Pill>
            </Stack>
          ))}
        </>
      )}
      {type === 'finding' && (
        <>
          <Pill color={SEV_COLOR[r.severity]}>{r.severity.toUpperCase()}</Pill>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{(r.description || '').slice(0, 300)}</Typography>
          {r.evidence && <Alert severity="warning" sx={{ py: 0 }}>The evidence is included. Check it has no passwords or keys.</Alert>}
        </>
      )}
      {type === 'case' && (
        <>
          <Typography variant="body2" color="text.secondary">{r.status} · priority {r.priority}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{(r.description || '').slice(0, 300)}</Typography>
        </>
      )}
    </Stack>
  );
}

function CreateDialog({ open, onClose, onCreated }) {
  const [type, setType] = useState('scan');
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [days, setDays] = useState(7);
  const [label, setLabel] = useState('');
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setType('scan'); setQ(''); setPicked(null); setDays(7); setLabel(''); setErr(''); } }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    setPicked(null);
    const t = setTimeout(() => sharedLinkOptions(type, q).then(setOptions).catch(() => setOptions([])), 250);
    return () => clearTimeout(t);
  }, [open, type, q]);
  useEffect(() => {
    setPreview(null);
    if (picked) sharedLinkPreview(type, picked.id).then(setPreview).catch(() => setPreview(null));
  }, [picked, type]);

  const create = async () => {
    try {
      await createSharedLink({ resource_type: type, resource_id: picked.id, label: label.trim(), expires_at: expiryAt(days) });
      onCreated();
    } catch (e) { setErr(fail(e, 'Could not create the link.')); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>Create a link</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={3} sx={{ pt: 1 }}>
          <Stack spacing={1.6} sx={{ flex: 1.1 }}>
            {err && <Alert severity="error">{err}</Alert>}
            <Stack direction="row" gap={1}>{TYPES.map((t) => <Choice key={t.id} on={type === t.id} onClick={() => { setType(t.id); setQ(''); }}>{t.label}</Choice>)}</Stack>
            <TextField size="small" placeholder={`Search your ${type}s`} value={q} onChange={(e) => setQ(e.target.value)} />
            <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
              {!options.length && <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Nothing to share here yet.</Typography>}
              {options.map((o) => (
                <Stack key={o.id} direction="row" gap={1} onClick={() => setPicked(o)} sx={{ p: 1.2, mb: 0.6, borderRadius: 2, cursor: 'pointer', border: 1, borderColor: picked?.id === o.id ? GREEN : 'divider', bgcolor: picked?.id === o.id ? alpha(GREEN, 0.08) : 'transparent' }}>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{o.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.detail}</Typography>
                </Stack>
              ))}
            </Box>
            <Typography variant="overline" color="text.secondary">Available for</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">{EXPIRY.map((x) => <Choice key={x.id} on={days === x.id} onClick={() => setDays(x.id)}>{x.label}</Choice>)}</Stack>
            <TextField size="small" label="Label (optional)" placeholder="For the auditor" value={label} onChange={(e) => setLabel(e.target.value)} inputProps={{ maxLength: 200 }} />
          </Stack>
          <Box sx={{ ...panel, p: 2.5, flex: 1, minHeight: 240 }}>
            {preview && picked ? <Preview type={type} data={preview} label={label.trim()} /> : <Typography color="text.secondary">Pick something on the left to see what the other person will see.</Typography>}
          </Box>
        </Stack>
        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ mt: 2.5, pb: 1 }}>
          <Button color="inherit" onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="success" disabled={!picked} onClick={create}>Create link</Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function SharedLinksPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try { const r = await listSharedLinks(); setLinks(Array.isArray(r) ? r : r.results || []); } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load your links.') }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (l) => {
    const url = l.public_url || `${window.location.origin}/shared/${l.token}`;
    try { await navigator.clipboard.writeText(url); setToast({ severity: 'success', message: 'Link copied.' }); } catch { window.prompt('Copy the link:', url); }
  };
  const withdraw = async (l) => {
    if (!window.confirm('Withdraw this link? It stops working at once.')) return;
    try { await revokeSharedLink(l.id); setToast({ severity: 'success', message: 'Link withdrawn.' }); } catch (e) { setToast({ severity: 'error', message: fail(e, 'That did not work.') }); }
    load();
  };
  const remove = async (l) => {
    if (!window.confirm('Delete this link from the list?')) return;
    try { await deleteSharedLink(l.id); } catch (e) { setToast({ severity: 'error', message: fail(e, 'That did not work.') }); }
    load();
  };

  const sum = useMemo(() => summary(links), [links]);
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 190px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Shared links</Typography>
        <Typography color="text.secondary" variant="body2">Let an auditor or a colleague read one scan, finding or case. No account needed</Typography>
      </Box>
      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Active links', sum.active, 'anyone with the link can read', GREEN)}
        {stat('Views', sum.views, 'across all links')}
        {stat('Expiring soon', sum.soon, 'within 7 days', sum.soon ? '#fcd34d' : undefined)}
      </Stack>

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.2 }}>
          <Typography sx={{ fontWeight: 700 }}>Your links</Typography>
          <Button variant="contained" onClick={() => setCreating(true)} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>New link</Button>
        </Stack>
        {!links.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>No links yet. Make one to let someone read a scan, finding or case without an account.</Typography>}
        {links.map((l) => {
          const st = STATE[stateOf(l)];
          const live = stateOf(l) === 'active';
          return (
            <Stack key={l.id} direction="row" alignItems="center" gap={2.5} flexWrap="wrap" sx={{ px: 2.8, py: 1.8, borderTop: 1, borderColor: 'divider', opacity: live ? 1 : 0.65 }}>
              <Box sx={{ flex: '2 1 280px', minWidth: 0 }}>
                <Stack direction="row" gap={1} alignItems="center">
                  <Pill color={KIND_COLOR[l.resource_type]}>{l.resource_type.toUpperCase()}</Pill>
                  <Typography sx={{ fontWeight: 600 }} noWrap>{l.resource_title || 'No longer available'}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">{l.label ? `Label: ${l.label}` : 'No label'}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 150px' }}>
                <Typography variant="body2">{expiryText(l)}</Typography>
                <Typography variant="caption" color="text.secondary">{viewsText(l.access_count)}{l.last_accessed_at ? ` · viewed ${ago(l.last_accessed_at)}` : ''}</Typography>
              </Box>
              <Pill color={st.color}>{st.label}</Pill>
              <Stack direction="row" gap={0.8}>
                {live && <Button size="small" variant="contained" color="success" onClick={() => copy(l)}>Copy link</Button>}
                {live && <Button size="small" variant="outlined" color="inherit" onClick={() => withdraw(l)}>Withdraw</Button>}
                {!live && <Button size="small" variant="outlined" color="inherit" onClick={() => remove(l)}>Delete</Button>}
              </Stack>
            </Stack>
          );
        })}
      </Box>
      <Typography variant="body2" color="text.secondary">Anyone with the link can read it without signing in. They cannot change anything. Withdraw a link at any time and it stops working at once.</Typography>

      <CreateDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); setToast({ severity: 'success', message: 'Link created. Copy it from the list.' }); load(); }} />
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
